import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';

interface AppBridge {
  version?: () => Promise<string>;
  update?: () => Promise<boolean>;
  onUpdateProgress?: (fn: (pct: number) => void) => () => void;
}

const bridge = (): AppBridge | null => (typeof window !== 'undefined' ? ((window as Window & { keoraApp?: AppBridge }).keoraApp ?? null) : null);

/** «1.10.0» > «1.9.2»: compara número por número. */
export function newer(latest: string, installed: string): boolean {
  const a = latest.split('.').map((n) => parseInt(n, 10) || 0);
  const b = installed.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) > (b[i] ?? 0);
  }
  return false;
}

/** Versión instalada: la app la dice; las versiones viejas solo la traen en su identificador. */
async function installedVersion(app: AppBridge): Promise<string | null> {
  if (app.version) return app.version().catch(() => null);
  return /KeoraProyeccion\/([\d.]+)/.exec(navigator.userAgent)?.[1] ?? null;
}

/** Cada cuánto la app pregunta si hay versión nueva. */
const CHECK_EVERY_MS = 60_000;

export const DOWNLOAD_URL = '/api/media/downloads/keora-multimedia/file';

/**
 * ¿Hay una versión nueva de KEORA Multimedia? La administración de KEORA sube cada versión en
 * Multimedia; la app la compara con la suya y, con «Actualizar», la descarga e instala sola.
 */
export function useAppUpdate() {
  const [installed, setInstalled] = useState<string | null>(null);
  const [latest, setLatest] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const app = bridge();

  useEffect(() => {
    if (!app) return;
    let alive = true;
    void installedVersion(app).then((v) => alive && setInstalled(v));
    const check = () =>
      void api
        .get<{ download: { version: string } | null }>('/media/downloads/keora-multimedia')
        .then((r) => alive && setLatest(r.download?.version ?? null))
        .catch(() => undefined);
    check();
    // En cuanto se sube una versión nueva aparece «Actualizar», sin recargar la app.
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    window.addEventListener('focus', check);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = Boolean(installed && latest && newer(latest, installed));
  /** Las versiones anteriores a 1.3.0 no saben actualizarse solas: se descarga el instalador. */
  const automatic = Boolean(app?.update);

  async function update() {
    if (!app?.update) return;
    setError(null);
    setProgress(0);
    const stop = app.onUpdateProgress?.((pct) => setProgress(pct));
    try {
      await app.update();
    } catch (e) {
      setError(errorMessage(e));
      setProgress(null);
    } finally {
      stop?.();
    }
  }

  return { installed, latest, available, automatic, progress, error, update };
}
